import { useActivityFeedback } from '@/libs/fleet-api/services/robotic';
import { Button, DatePicker, Descriptions, Input, Modal, Select, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { FC, useEffect, useState } from 'react';

type Props = {
	open: boolean;
	activityDetailId: number | null;
	onSubmit: (field: string, value: string) => void;
	onCancel: () => void;
	loading?: boolean;
};

const EditPendingFieldModal: FC<Props> = ({
	open,
	activityDetailId,
	onSubmit,
	onCancel,
	loading,
}) => {
	const [value, setValue] = useState('');

	const feedbackQuery = useActivityFeedback({
		roboticActivityId: open ? activityDetailId : null,
	});

	const feedback = feedbackQuery.data?.data;
	const isFetching = feedbackQuery.isFetching;

	useEffect(() => {
		if (open && feedback?.currentValue) {
			setValue(feedback.currentValue);
		} else if (open) {
			setValue('');
		}
	}, [open, feedback?.currentValue]);

	const handleSubmit = () => {
		if (!feedback?.field) return;
		onSubmit(feedback.field, value);
	};

	const renderInput = () => {
		const fieldType = feedback?.fieldType?.toLowerCase() ?? 'text';
		if (fieldType === 'select' && feedback?.options?.length) {
			return (
				<Select
					style={{ width: '100%' }}
					value={value || undefined}
					onChange={(v) => setValue(v)}
					options={feedback.options.map((o) => ({
						label: o.value ?? String(o.id),
						value: String(o.id),
					}))}
					disabled={loading}
					placeholder="Pilih nilai"
				/>
			);
		}
		if (fieldType === 'date') {
			return (
				<DatePicker
					style={{ width: '100%' }}
					value={value ? dayjs(value) : undefined}
					onChange={(d) => setValue(d ? d.format('YYYY-MM-DD') : '')}
					format="DD/MM/YYYY"
					disabled={loading}
				/>
			);
		}
		return (
			<Input
				value={value}
				onChange={(e) => setValue(e.target.value)}
				placeholder="Masukkan nilai baru"
				disabled={loading}
			/>
		);
	};

	return (
		<Modal
			open={open}
			onCancel={onCancel}
			footer={null}
			closable={!loading}
			maskClosable={false}
			title="Edit Pending Field"
			width={520}
		>
			{isFetching ? (
				<div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
					<Spin />
				</div>
			) : (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
					<Descriptions bordered size="small" column={1}>
						<Descriptions.Item label="Pending Step">
							{feedback?.pendingStep ?? '-'}
						</Descriptions.Item>
						<Descriptions.Item label="Pending Field">
							{feedback?.formLabel ?? feedback?.pendingField ?? '-'}
						</Descriptions.Item>
						<Descriptions.Item label="Pending Message">
							<Typography.Text style={{ color: 'red' }}>
								{feedback?.pendingMessage ?? '-'}
							</Typography.Text>
						</Descriptions.Item>
					</Descriptions>

					<div>
						<Typography.Text strong>Nilai Baru</Typography.Text>
						<div style={{ marginTop: 4 }}>{renderInput()}</div>
					</div>

					<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
						<Button onClick={onCancel} disabled={loading}>
							Kembali
						</Button>
						<Button
							type="primary"
							loading={loading}
							disabled={!value.trim() || !feedback?.field}
							onClick={handleSubmit}
						>
							Update
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default EditPendingFieldModal;
